// Argus — FilePushProgressPanel: floating panel pentru DnD upload progress.
// Bloc file-push-deployment 2026-05-07.
//
// Layout: floating bottom-right (similar bulkDownload existing pattern), expand/collapse,
// global progress bar (% bytes uploaded total), per-file list cu Status chip + per-file bar.
// Cancel button → caller aborts via AbortController.

import { useMemo } from 'react';
import { Box, IconButton, LinearProgress, Paper, Typography, Chip, Button, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BlockIcon from '@mui/icons-material/Block';
import { Virtuoso } from 'react-virtuoso';
import { formatBytes } from '../../lib/dirListing';
import type { PushItem, PushItemStatus } from '../../lib/filePush';

interface Props {
  items: readonly PushItem[];
  pcName: string;
  onCancel: () => void;
  onClose: () => void;
}

const STATUS_COLOR: Record<PushItemStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  uploading: 'primary',
  done: 'success',
  failed: 'error',
  cancelled: 'warning',
};

function StatusIcon({ status }: { status: PushItemStatus }): JSX.Element {
  switch (status) {
    case 'pending': return <HourglassEmptyIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
    case 'uploading': return <CloudUploadIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    case 'done': return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    case 'failed': return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'cancelled': return <BlockIcon fontSize="small" sx={{ color: 'warning.main' }} />;
  }
}

export function FilePushProgressPanel({ items, pcName, onCancel, onClose }: Props): JSX.Element {
  const totals = useMemo(() => {
    let totalBytes = 0;
    let uploadedBytes = 0;
    let pending = 0;
    let uploading = 0;
    let done = 0;
    let failed = 0;
    let cancelled = 0;
    for (const it of items) {
      totalBytes += it.size;
      uploadedBytes += it.bytesUploaded;
      switch (it.status) {
        case 'pending': pending++; break;
        case 'uploading': uploading++; break;
        case 'done': done++; break;
        case 'failed': failed++; break;
        case 'cancelled': cancelled++; break;
      }
    }
    return { totalBytes, uploadedBytes, pending, uploading, done, failed, cancelled };
  }, [items]);

  const progressPct = totals.totalBytes > 0 ? (totals.uploadedBytes / totals.totalBytes) * 100 : 0;
  const allFinished = totals.pending === 0 && totals.uploading === 0;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        width: 380,
        maxHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <CloudUploadIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Pushing {items.length} {items.length === 1 ? 'item' : 'items'} → {pcName}
        </Typography>
        {!allFinished && (
          <Tooltip title="Cancel all pending transfers">
            <Button size="small" color="warning" onClick={onCancel}>Cancel</Button>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onClose} disabled={!allFinished} title={allFinished ? 'Close' : 'Wait for transfers to finish or cancel first'}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <LinearProgress variant="determinate" value={progressPct} sx={{ height: 6, borderRadius: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {formatBytes(totals.uploadedBytes)} / {formatBytes(totals.totalBytes)}
          {' · '}
          <span style={{ color: 'inherit' }}>{totals.done} done</span>
          {totals.failed > 0 && <span style={{ color: 'var(--mui-palette-error-main)' }}>{` · ${totals.failed} failed`}</span>}
          {totals.cancelled > 0 && <span style={{ color: 'var(--mui-palette-warning-main)' }}>{` · ${totals.cancelled} cancelled`}</span>}
          {totals.uploading > 0 && <span style={{ color: 'var(--mui-palette-primary-main)' }}>{` · ${totals.uploading} uploading`}</span>}
          {totals.pending > 0 && <span style={{ color: 'var(--mui-palette-text-disabled)' }}>{` · ${totals.pending} pending`}</span>}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Virtuoso
          style={{ height: 280 }}
          data={items as PushItem[]}
          itemContent={(_idx, it) => {
            const filePct = it.size > 0 ? (it.bytesUploaded / it.size) * 100 : 0;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                <StatusIcon status={it.status} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Tooltip title={it.relativePath}>
                    <Typography variant="body2" noWrap>{it.file.name}</Typography>
                  </Tooltip>
                  {it.status === 'uploading' && (
                    <LinearProgress variant="determinate" value={filePct} sx={{ height: 3, mt: 0.25 }} />
                  )}
                  {it.error && (
                    <Typography variant="caption" color="error.main" noWrap>{it.error}</Typography>
                  )}
                </Box>
                <Chip
                  label={it.status === 'uploading' ? `${Math.round(filePct)}%` : it.status}
                  size="small"
                  color={STATUS_COLOR[it.status]}
                  sx={{ minWidth: 60 }}
                />
              </Box>
            );
          }}
        />
      </Box>
    </Paper>
  );
}
